import containerQueries from "@tailwindcss/container-queries";
import { Config } from "tailwindcss";
import defaultTheme from "tailwindcss/defaultTheme";
import plugin from "tailwindcss/plugin";

import { METAC_COLORS } from "./src/constants/colors";

const config: Config = {
  // Note: .force-light only suppresses Tailwind-generated dark: utilities.
  // Hand-written .dark selectors in CSS files (e.g. KaTeX, markdown styles)
  // are not affected and will still match the .dark class on <html>.
  darkMode: [
    "variant",
    [
      // Dark mode: inside .dark, but not the .inverted/.force-light element or its children
      "&:is(.dark *):not(:is(.inverted, .inverted *, .force-light, .force-light *))",
      // Inverted in light mode: the .inverted element itself OR its children, but not in dark mode or force-light
      "&:is(.inverted, .inverted *):not(:is(.dark *, .force-light, .force-light *))",
    ],
  ],
  content: [
    "./src/pages/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/components/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/app/**/*.{js,ts,jsx,tsx,mdx}",
    "./src/utils/**/*.{js,ts,jsx,tsx,mdx}",
  ],
  theme: {
    screens: Object.assign({ xs: "480px", xxs: "400px" }, defaultTheme.screens),
    extend: {
      colors: METAC_COLORS,
      boxShadow: {
        dropdown: `2px 3px 10px -3px ${METAC_COLORS.gray[500]}`,
      },
      keyframes: {
        "loading-slide": {
          from: { transform: "translateX(100%)" },
          to: { transform: "translateX(-100%)" },
        },
        spin: {
          "0%": { transform: "rotate(0deg)" },
          "90%, 100%": { transform: "rotate(360deg)" },
        },
        "highlight-flash": {
          "0%": { backgroundColor: "rgb(196 180 255 / 0.5)" },
          "50%": { backgroundColor: "rgb(196 180 255 / 0.8)" },
          "100%": { backgroundColor: "transparent" },
        },
        "fade-in": {
          from: { opacity: "0" },
          to: { opacity: "1" },
        },
        "fade-out": {
          from: { opacity: "1" },
          to: { opacity: "0" },
        },
        "comment-in-right": {
          from: { opacity: "0", transform: "translateX(28px)" },
          to: { opacity: "1", transform: "translateX(0)" },
        },
        "comment-in-left": {
          from: { opacity: "0", transform: "translateX(-28px)" },
          to: { opacity: "1", transform: "translateX(0)" },
        },
      },
      animation: {
        "loading-slide":
          "loading-slide cubic-bezier(0.3, 1, 0.7, 0) 1.7s infinite",
        spin: "spin 1s infinite",
        "highlight-flash": "highlight-flash 2s ease-out forwards",
        "fade-in": "fade-in 200ms ease-out",
        "fade-out": "fade-out 300ms ease-out forwards",
        "comment-in-right": "comment-in-right 280ms ease-out",
        "comment-in-left": "comment-in-left 280ms ease-out",
      },
      fontFamily: {
        sans: [
          "var(--font-inter-variable)",
          "var(--font-inter)",
          ...defaultTheme.fontFamily.sans,
        ],
        serif: [
          "var(--font-source-serif-pro)",
          ...defaultTheme.fontFamily.serif,
        ],
        mono: ['"Ubuntu mono"', ...defaultTheme.fontFamily.mono],
        "league-gothic": "var(--font-league-gothic)",
        geist: ["var(--font-geist)", ...defaultTheme.fontFamily.sans],
        "geist-mono": [
          "var(--font-geist-mono)",
          ...defaultTheme.fontFamily.mono,
        ],
        "jetbrains-mono": [
          "var(--font-jetbrains-mono)",
          ...defaultTheme.fontFamily.mono,
        ],
        newsreader: [
          "var(--font-newsreader)",
          ...defaultTheme.fontFamily.serif,
        ],
      },
      strokeWidth: {
        "3": "3px",
      },
      scrollMargin: {
        nav: "70px",
      },
      zIndex: {
        "100": "100",
        header: "210",
        modal: "220",
        popover: "230",
      },
      backgroundImage: {
        "border-dashed-1":
          "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23A9C0D6FF' stroke-width='1' stroke-dasharray='4%2c 6' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")",
        "border-dashed-1-dark":
          "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%236387A8FF' stroke-width='1' stroke-dasharray='4%2c 6' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")",
      },
      spacing: {
        header: "var(--top-chrome-height,3rem)",
      },
      borderRadius: {
        xs: "2px",
      },
    },
  },
  plugins: [
    containerQueries,
    // @ts-expect-error - addVariant is typed as any
    function ({ addVariant }) {
      addVariant("no-hover", "@media (hover: none)");
      addVariant("can-hover", "@media (hover: hover)");
    },
    plugin(function ({ addUtilities }) {
      addUtilities({
        /* https://github.com/tailwindlabs/tailwindcss/pull/12128 */
        ".break-anywhere": {
          "overflow-wrap": "anywhere",
        },
        /* Hide scrollbar for Chrome, Safari and Opera */
        ".no-scrollbar::-webkit-scrollbar": {
          display: "none",
        },
        /* Hide scrollbar for IE, Edge and Firefox */
        ".no-scrollbar": {
          "-ms-overflow-style": "none" /* IE and Edge */,
          "scrollbar-width": "none" /* Firefox */,
        },
        ".fill-opacity-50": {
          "fill-opacity": "0.5",
        },
        ".list-lower-alpha": {
          "list-style-type": "lower-alpha",
        },
        ".list-lower-roman": {
          "list-style-type": "lower-roman",
        },
        ".text-xs": {
          "text-rendering": "geometricPrecision",
        },
      });
    }),
  ],
};
export default config;
